import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { DashboardShell } from '../components/brand/DashboardShell';
import { Shield, Activity, Cpu, Lock, FileLock2, Server, CheckCircle2, RefreshCw, Camera } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICONS = {
  security: Shield,
  availability: Activity,
  processing_integrity: Cpu,
  confidentiality: Lock,
  privacy: FileLock2,
  operations: Server,
};

const fmt = (v) => {
  if (v === true) return 'Yes';
  if (v === false) return 'No';
  if (Array.isArray(v)) return v.join(', ');
  if (typeof v === 'number') return v.toLocaleString();
  return String(v);
};

/**
 * /trust/evidence — SOC 2 Evidence Collector (Blueprint Decision #48).
 *
 * Live read of every TSC bucket: each number comes from a real DB
 * counter or system property. Used by sales for enterprise diligence
 * sessions + the audit firm during fieldwork.
 */
export default function TrustEvidence() {
  const { getAuthHeader } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [snapshotting, setSnapshotting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await axios.get(`${API}/trust/evidence/summary`, getAuthHeader());
      setData(d);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load SOC 2 evidence');
    } finally {
      setLoading(false);
    }
  };

  const snapshot = async () => {
    setSnapshotting(true);
    try {
      await axios.post(`${API}/trust/evidence/snapshot`, {}, getAuthHeader());
      toast.success('Evidence snapshot captured for auditor review.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Snapshot failed');
    } finally {
      setSnapshotting(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  return (
    <DashboardShell data-testid="trust-evidence-page">
      <header className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="cn-eyebrow inline-flex items-center gap-1.5">
            <Shield className="w-3 h-3" /> Compliance · Trust
          </div>
          <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight">SOC 2 Evidence</h1>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">
            Live evidence aggregated from the running system, mapped to the SOC 2 Trust Services Criteria.
            Every number is a real DB counter — verifiable against the operational graph in real time.
          </p>
          {data && (
            <div className="mt-3 flex items-center gap-3 text-xs text-slate-500">
              <span>Target: <span className="font-semibold text-slate-700">{data.soc2_target}</span></span>
              <span>·</span>
              <span>HITRUST: <span className="font-semibold text-slate-700">{data.hitrust_target}</span></span>
            </div>
          )}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} disabled={loading} data-testid="evidence-refresh-btn">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </Button>
          <Button onClick={snapshot} disabled={snapshotting} className="bg-cn-primary text-white hover:bg-cn-primary-hover" data-testid="evidence-snapshot-btn">
            <Camera className="w-4 h-4 mr-2" /> {snapshotting ? 'Capturing…' : 'Capture snapshot'}
          </Button>
        </div>
      </header>

      {loading ? (
        <div className="rounded-2xl bg-white border border-slate-100 p-8 text-slate-500">Loading SOC 2 evidence…</div>
      ) : !data ? (
        <div className="rounded-2xl bg-white border border-slate-100 p-8 text-slate-500">No data.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {data.sections.map((s) => {
            const Icon = ICONS[s.id] || Shield;
            return (
              <section key={s.id} className="rounded-2xl bg-white border border-slate-100 p-6" data-testid={`section-${s.id}`}>
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <span className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-cn-primary/10 text-cn-primary">
                      <Icon className="w-5 h-5" />
                    </span>
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-slate-500">{s.tsc}</div>
                      <h2 className="font-semibold text-slate-900">{s.title}</h2>
                    </div>
                  </div>
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-2" />
                </div>
                <dl className="space-y-2">
                  {Object.entries(s.evidence).map(([k, v]) => (
                    <div key={k} className="flex justify-between items-baseline py-1.5 border-b border-slate-100 last:border-0 gap-3">
                      <dt className="text-xs text-slate-500 capitalize flex-1 min-w-0">{k.replace(/_/g, ' ')}</dt>
                      <dd className={`text-sm font-semibold tabular-nums text-right ${typeof v === 'boolean' ? (v ? 'text-emerald-700' : 'text-rose-700') : 'text-slate-900'}`}>
                        {fmt(v)}
                      </dd>
                    </div>
                  ))}
                </dl>
              </section>
            );
          })}
        </div>
      )}

      {data && (
        <p className="text-[10px] text-slate-400 mt-6">
          Generated {new Date(data.generated_at).toLocaleString()} · Evidence reflects live system state at request time.
        </p>
      )}
    </DashboardShell>
  );
}
